import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { JSONPath } from 'jsonpath-plus';
import { debounce } from 'lodash';
import { compose, trimCharsEnd, trimCharsStart } from 'lodash/fp';
import React, { useState } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import ReactModal from 'react-modal';
import Button from '../../../components/Button';
import Error from '../../../components/Error';
import Modal, { MODAL_STYLES } from '../../../components/Modal';
import { Pill } from '../../../components/Pill';
import CustomData from '../../../types/customData';
import { useAxios } from '../../../utils/hooks';
import VALIDATORS from '../../../utils/validators';

const trimQuotes = compose(trimCharsEnd('"'), trimCharsStart('"'));

interface Props {
    customData: CustomData[];
    isOpen: boolean;
    onClose: () => void;
    onSave: (customData: CustomData) => void;
    onDelete: (customData: CustomData) => void;
}

export default function CustomDataModal({ customData, isOpen, onClose, onSave, onDelete }: Props) {
    const [deleting, setDeleting] = useState<CustomData>();
    const handleConfirmDelete = () => {
        if (deleting) {
            onDelete(deleting);
        }
        setDeleting(undefined);
    };
    const handleCancelDelete = () => {
        setDeleting(undefined);
    };
    return (
        <ReactModal isOpen={isOpen} style={MODAL_STYLES}>
            {deleting && (
                <Modal
                    type="WARN"
                    title="Delete Custom Data"
                    body={
                        <>
                            Are you sure you want to delete this Custom Data? All of your data will be permanently
                            removed. This action cannot be undone.
                        </>
                    }
                    confirmButtonText="Delete"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
            {!deleting && (
                <Modal
                    type="INFO"
                    title="Custom Data"
                    body={<ModalBody customData={customData} onSave={onSave} onDelete={setDeleting} />}
                    confirmButtonText="Close"
                    onConfirm={onClose}
                />
            )}
        </ReactModal>
    );
}

function ModalBody({ customData, onSave, onDelete }: Pick<Props, 'customData' | 'onSave' | 'onDelete'>) {
    const [showForm, setShowForm] = useState(false);
    const [editingData, setEditingData] = useState<CustomData>();
    const handleEdit = (customData: CustomData) => {
        setEditingData(customData);
        setShowForm(true);
    };
    const handleCreate = () => {
        setEditingData(undefined);
        setShowForm(true);
    };
    const handleCancel = () => {
        setEditingData(undefined);
        setShowForm(false);
    };

    return (
        <>
            {!showForm && (
                <ListData customData={customData} onEdit={handleEdit} onCreate={handleCreate} onDelete={onDelete} />
            )}
            {showForm && <CustomDataForm onSave={onSave} onCancel={handleCancel} data={editingData} />}
        </>
    );
}

function ListData(
    props: Pick<Props, 'customData' | 'onDelete'> & {
        onEdit: (customData: CustomData) => void;
        onCreate: () => void;
    }
) {
    return (
        <div className="flex flex-col">
            {props.customData.map((data) => (
                <div key={data.id} className="flex flex-row">
                    <div className="p-2 ml-2 mb-2 border border-gray-300 text-gray-700 rounded-md transition duration-500 ease select-none hover:bg-gray-300 focus:outline-none focus:shadow-outline">
                        {`{{${data.name}}}`}
                    </div>
                    <button onClick={() => props.onEdit(data)} className="ml-2 pointer text-black hover:text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1792 1792" className="w-4 fill-current">
                            <path d="M491 1536l91-91-235-235-91 91v107h128v128h107zm523-928q0-22-22-22-10 0-17 7l-542 542q-7 7-7 17 0 22 22 22 10 0 17-7l542-542q7-7 7-17zm-54-192l416 416-832 832H128v-416zm683 96q0 53-37 90l-166 166-416-416 166-165q36-38 90-38 53 0 91 38l235 234q37 39 37 91z" />
                        </svg>
                    </button>
                    <button onClick={() => props.onDelete(data)} className="pointer">
                        <img src="/images/close.svg" alt="" className="ml-2 w-4" />
                    </button>
                </div>
            ))}
            <Button kind="tertiary" onClick={props.onCreate}>
                Add Custom Data
            </Button>
        </div>
    );
}

interface CustomDataFormData extends Omit<CustomData, 'id'> {
    id?: string;
    testValues: string[];
}

function CustomDataForm(props: Pick<Props, 'onSave'> & { data?: CustomData; onCancel: () => void }) {
    const axios = useAxios();
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState<{ status?: number; data?: any }>();
    const [verified, setVerified] = useState(false);
    const handleTestUrl = async (values: CustomDataFormData) => {
        if (!axios.current) {
            return;
        }
        setVerifying(true);
        try {
            const { data, status } = await axios.current.post<string>('/custom-data/test', values);
            setVerifyResult({ status, data });
            setVerified(status === 200);
        } catch (err: any) {
            setVerifyResult({ status: err?.response?.status, data: undefined });
        } finally {
            setVerifying(false);
        }
    };
    const handleResetValue = () => {
        setVerified(false);
        setVerifyResult(undefined);
    };
    const updateTestFields = debounce(
        (value: string, form: FormApi<CustomDataFormData, Partial<CustomDataFormData>>) => {
            form.change(
                'testValues',
                Array.from(value.matchAll(/{{EMAIL}}|{{STRIPE_ID}}/g)).map((value) => value[0])
            );
        },
        1000
    );
    const handleSubmit = async ({ testValues, ...customData }: CustomDataFormData) => {
        if (!axios.current) {
            return;
        }
        const { data } = await await axios.current.post<CustomData>('/custom-data', { ...customData });
        props.onSave(data);
    };

    return (
        <Form<CustomDataFormData>
            onSubmit={handleSubmit}
            mutators={{
                ...(arrayMutators as any),
            }}
            initialValues={props.data ? props.data : {}}
        >
            {({ handleSubmit, invalid, valid, values, form }) => (
                <>
                    <p className="mb-2">
                        Custom Data lets you pull data from another system to use in your Customer Portal or Pricing
                        Page.
                    </p>
                    <div className="mb-2">
                        <Pill title="Tip" />
                    </div>
                    <div>
                        Use <pre className="inline">{'{{EMAIL}}'}</pre> or{' '}
                        <pre className="inline">{'{{STRIPE_ID}}'}</pre> as placeholders for your customers' email or
                        Stripe Id.
                    </div>
                    <p className="mb-4">
                        Example: <span>https://myapp.com/api/data?email={'{{EMAIL}}'}</span>
                    </p>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">Name</label>
                        <Field name="name" component="input" type="text" validate={VALIDATORS.required}>
                            {({ input, meta }) => (
                                <>
                                    <input {...input} placeholder="My Custom Data" className="input w-full" />
                                    {meta.error && (
                                        <span className="ml-2">
                                            <Error message={meta.error} />
                                        </span>
                                    )}
                                </>
                            )}
                        </Field>
                    </div>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">Url</label>
                        <Field name="url" component="input" type="url">
                            {({ input: { onChange, ...input }, meta }) => (
                                <>
                                    <div className="flex flex-wrap items-stretch w-full mb-4 relative">
                                        <input
                                            onChange={(event) => {
                                                updateTestFields(event.currentTarget.value, form);
                                                onChange(event);
                                            }}
                                            {...input}
                                            placeholder="https://myapp.com/api/data?email={{EMAIL}}"
                                            className="input rounded-r-none flex-shrink flex-grow flex-auto leading-normal w-px flex-1"
                                        />
                                        <div className="flex -mr-px">
                                            <button
                                                onClick={() => {
                                                    form.change('url', undefined);
                                                    handleResetValue();
                                                }}
                                                className="flex items-center leading-normal bg-grey-lighter rounded rounded-l-none border border-l-0 border-grey-light px-3 whitespace-no-wrap text-grey-dark text-sm bg-gray-200 hover:bg-gray-400"
                                                disabled={!values.url}
                                            >
                                                <img src="/images/close.svg" alt="" className="w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    {meta.error && (
                                        <span className="ml-2">
                                            <Error message={meta.error} />
                                        </span>
                                    )}
                                    {verifyResult?.status && verifyResult?.status !== 200 && (
                                        <span className="ml-2">
                                            <Error message={`${verifyResult.status}`} />
                                        </span>
                                    )}
                                </>
                            )}
                        </Field>
                    </div>
                    <div className="mb-4 ml-4">
                        <div className="mb-2">
                            <FieldArray name="testValues">
                                {({ fields }) =>
                                    fields.map((name, index) => (
                                        <div key={name}>
                                            <label className="text-sm text-gray-600">Test Data {index + 1}</label>
                                            <Field name={`${name}`} component="input" type="text">
                                                {({ input }) => <input {...input} className="input w-full" />}
                                            </Field>
                                        </div>
                                    ))
                                }
                            </FieldArray>
                        </div>
                        <div className="flex flex-row-reverse">
                            <Button
                                onClick={() => handleTestUrl(values)}
                                kind="tertiary"
                                disabled={invalid || verifying}
                                state={verifying ? 'SAVING' : 'READY'}
                            >
                                Test Url {verifyResult?.data ? 'Again' : ''}
                            </Button>
                        </div>
                        {verifyResult?.data && (
                            <div className="mb-2">
                                <label className="text-sm text-gray-600">Result</label>
                                <textarea
                                    className="input w-full h-32 bg-gray-100"
                                    value={JSON.stringify(verifyResult.data, null, 2)}
                                    disabled
                                ></textarea>
                            </div>
                        )}
                    </div>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">
                            JSONPath <span className="text-gray-500">(Optional)</span>
                        </label>
                        <Field name="jsonPath" component="input" type="text">
                            {({ input }) => (
                                <input {...input} placeholder="$.store.books[1]" className="input w-full" />
                            )}
                        </Field>
                    </div>
                    <div className="my-2">
                        <Field name="settings.hidePortalHeadlineWhenBlank" type="checkbox">
                            {(props) => (
                                <label className="inline-flex items-center">
                                    <input {...props.input} type="checkbox" className="checkbox" defaultValue="false" />
                                    <span className="ml-2 text-gray-700">
                                        Hide Customer Portal headline when data is blank
                                    </span>
                                </label>
                            )}
                        </Field>
                    </div>
                    <div className="ml-4 mb-2">
                        {verifyResult?.data && values.jsonPath && (
                            <label className="text-sm text-gray-600">Result</label>
                        )}

                        <FormSpy
                            subscription={{ values: true }}
                            render={({ values }) => {
                                if (verifyResult?.data && values.jsonPath) {
                                    return (
                                        <div>
                                            <input
                                                value={trimQuotes(
                                                    JSON.stringify(
                                                        JSONPath({
                                                            path: values.jsonPath,
                                                            json: verifyResult.data,
                                                            preventEval: true,
                                                            flatten: true,
                                                        })[0]
                                                    )
                                                )}
                                                className="input w-full bg-gray-100"
                                                type="text"
                                                disabled
                                            />
                                        </div>
                                    );
                                } else {
                                    return null;
                                }
                            }}
                        />
                    </div>
                    <div className="flex flex-row-reverse">
                        {valid && verified && (
                            <Button onClick={handleSubmit} disabled={invalid} className="ml-2">
                                Save Custom Data
                            </Button>
                        )}
                        <Button kind="tertiary" onClick={props.onCancel}>
                            Cancel
                        </Button>
                    </div>
                </>
            )}
        </Form>
    );
}
